import {
  FC,
  PropsWithChildren,
  useDebugValue,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import {
  FormProvider,
  useForm,
  useFormContext as _useFormContext,
  useController as _useController,
  useFormState as _useFormState,
  UseControllerProps,
  FieldPath,
  FieldArrayPath,
  UseFormStateProps,
  FieldPathValue,
  FieldArray,
} from 'react-hook-form';
import { uniqueId } from 'lodash';
import { removeElementAt, replaceElementAt } from 'utils/arrayHelpers';
import { Combinator, ConditionalContent, NumericComparator, StringValue } from '../domain';

export type FormState = {
  conditionalContent?: ConditionalContent;
};

export const defaultCondition = {
  comparator: NumericComparator.Equals,
  fieldName: '',
  value: new StringValue(''),
  id: uniqueId(),
};

const defaultClause = {
  combinator: Combinator.AND,
  conditions: [defaultCondition],
  id: uniqueId(),
};

const defaultValues = {
  conditionalContent: {
    ifClause: defaultClause,
    elseIfClauses: [],
  },
};

export const FormContextProvider: FC<PropsWithChildren<NonNullable<unknown>>> = ({ children }) => {
  const form = useForm<FormState>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues,
  });

  return <FormProvider {...form}>{children}</FormProvider>;
};

export const useFormContext = () => _useFormContext<FormState>();

export const useController = <Name extends FieldPath<FormState>>(
  config: UseControllerProps<FormState, Name>,
) => {
  const { control } = useFormContext();
  const controller = _useController({ control, ...config });
  useDebugValue(controller);
  return controller;
};

export const useFormState = (config: UseFormStateProps<FormState>) => {
  const { control } = useFormContext();
  const formState = _useFormState<FormState>({ control, ...config });
  useDebugValue(formState);
  return formState;
};

export const useFieldState = (fieldName: FieldPath<FormState>) => {
  const {
    dirtyFields,
    errors,
    isDirty,
    isLoading,
    isSubmitSuccessful,
    isSubmitted,
    isSubmitting,
    isValid,
    isValidating,
    submitCount,
    touchedFields,
    defaultValues: defaults,
  } = useFormState({ name: fieldName });
  const { getFieldState } = useFormContext();
  const fieldState = useMemo(
    () => getFieldState(fieldName),
    [
      dirtyFields,
      errors,
      isDirty,
      isLoading,
      isSubmitSuccessful,
      isSubmitted,
      isSubmitting,
      isValid,
      isValidating,
      submitCount,
      touchedFields,
      defaults,
    ],
  );
  useDebugValue(fieldState);
  return fieldState;
};

export const useWatch = <P extends FieldPath<FormState>>(config: {
  name: P;
  disabled?: boolean;
}): FieldPathValue<FormState, P> => {
  const { getValues, watch } = useFormContext();
  const getValueFromFormContext = () => getValues(config.name) as FieldPathValue<FormState, P>;
  const [value, setLocalValue] = useState<FieldPathValue<FormState, P>>(getValueFromFormContext());
  const sync = () => setLocalValue(getValueFromFormContext());
  useEffect(() => {
    sync();
    const subscription = watch((_, { name }) => {
      if (name === config.name && !config.disabled) {
        sync();
      }
    });
    return subscription.unsubscribe;
  }, [config.name]);
  useDebugValue(value);
  return value;
};

export const useFieldArray = <Name extends FieldArrayPath<FormState>>(config: { name: Name }) => {
  const fields: FieldArray<FormState, Name>[] = useWatch({ name: config.name });
  const { setValue, getValues } = useFormContext();

  const append = useCallback(
    (field: FieldArray<FormState, Name>) => {
      const updatedFieldArray = [...getValues(config.name), field] as FieldPathValue<
        FormState,
        Name
      >;
      setValue(config.name, updatedFieldArray);
    },
    [setValue, getValues, config.name],
  );

  const remove = useCallback(
    (index: number) => {
      const updatedFieldArray = removeElementAt(getValues(config.name), index) as FieldPathValue<
        FormState,
        Name
      >;
      setValue(config.name, updatedFieldArray);
    },
    [setValue, getValues, config.name],
  );

  const update = useCallback(
    (index: number, field: FieldArray<FormState, Name>) => {
      const updatedFieldArray = replaceElementAt<FieldArray<FormState, Name>>(
        getValues(config.name),
        index,
        field,
      ) as FieldPathValue<FormState, Name>;
      setValue(config.name, updatedFieldArray);
    },
    [setValue, getValues, config.name],
  );

  return {
    fields,
    append,
    remove,
    update,
  };
};
